import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  X, 
  User, 
  FileText, 
  Search, 
  MessageSquare, 
  Handshake,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  CheckCircle2,
  Rocket
} from 'lucide-react';

const OnboardingWizard = ({ isOpen, onClose, user }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  
  const isRM = user?.role === 'resource_manager' || user?.role === 'operating_partner';
  const isFreelance = user?.is_freelance === true;

  // Don't render if not open
  if (!isOpen) return null;

  const steps = [
    {
      id: 'welcome',
      title: 'Welcome to Snow Exchange! 🎉',
      subtitle: isFreelance 
        ? 'Your platform to find ServiceNow projects as an independent consultant'
        : isRM 
          ? 'Your platform to place ServiceNow consultants and find projects'
          : 'Your platform to find the best ServiceNow consultants',
      icon: Rocket,
      color: 'from-emerald-400 to-teal-500',
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            We'll guide you through the key steps to get started and make the most of the platform.
          </p>
          <div className="grid grid-cols-3 gap-3 pt-2">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl mb-1">🎯</div>
              <p className="text-xs text-gray-600">AI Matching</p>
            </div>
            <div className="text-center p-3 bg-emerald-50 rounded-lg">
              <div className="text-2xl mb-1">💼</div>
              <p className="text-xs text-gray-600">Collaborations</p>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <div className="text-2xl mb-1">📄</div>
              <p className="text-xs text-gray-600">Contracts</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'profile',
      title: 'Complete Your Profile',
      subtitle: 'Build trust with other users',
      icon: User,
      color: 'from-blue-400 to-blue-600',
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            A complete profile helps you stand out and build credibility.
          </p>
          <ul className="space-y-2">
            {['Add your professional photo', 'Include your LinkedIn profile', 'Write a compelling bio', 'Add your company details'].map((item, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      ),
      action: { label: 'Edit Profile', path: '/profile' }
    },
    {
      id: 'create',
      title: isRM ? (isFreelance ? 'Create Your Consultant Profile' : 'Add Your First Resource') : 'Create Your First Project',
      subtitle: isRM ? 'Showcase ServiceNow expertise' : 'Define your resource needs',
      icon: FileText,
      color: 'from-emerald-400 to-emerald-600',
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            {isRM 
              ? isFreelance
                ? 'Create your consultant profile to appear in search results and receive matching project notifications.'
                : 'Add consultant profiles to showcase your team\'s ServiceNow skills and availability.'
              : 'Define your project requirements to attract the most qualified consultants.'}
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              <Sparkles className="w-4 h-4 inline mr-1" />
              <strong>Pro tip:</strong> {isRM 
                ? 'Include specific certifications and module expertise for better matches.'
                : 'Be specific about timelines and deliverables for better proposals.'}
            </p>
          </div>
        </div>
      ),
      action: { label: isRM ? 'Add Resource' : 'Create Project', path: isRM ? '/resources' : '/projects' }
    },
    {
      id: 'match',
      title: 'Discover Matches',
      subtitle: 'AI-powered compatibility scoring',
      icon: Search,
      color: 'from-purple-400 to-purple-600',
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            Our AI analyzes {isRM ? 'project requirements' : 'consultant profiles'} to find the best matches for you.
          </p>
          <div className="flex items-center justify-center gap-4 py-2">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold text-xl mx-auto mb-1">
                95%
              </div>
              <p className="text-xs text-gray-500">Match Score</p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-300" />
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-1">
                <MessageSquare className="w-6 h-6 text-gray-600" />
              </div>
              <p className="text-xs text-gray-500">Connect</p>
            </div>
          </div>
        </div>
      ),
      action: { label: 'Explore Matching', path: '/matching' }
    },
    {
      id: 'collaborate',
      title: 'Start Collaborating',
      subtitle: 'Manage projects end-to-end',
      icon: Handshake,
      color: 'from-orange-400 to-orange-600',
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            From initial contact to contract signing and project tracking — everything in one place.
          </p>
          <div className="grid grid-cols-4 gap-2 text-center">
            {[
              { emoji: '💬', label: 'Message' },
              { emoji: '📋', label: 'Pre-book' },
              { emoji: '📝', label: 'Contract' },
              { emoji: '🤝', label: 'Collaborate' }
            ].map((item, i) => (
              <div key={i} className="p-2 bg-gray-50 rounded-lg">
                <div className="text-xl mb-1">{item.emoji}</div>
                <p className="text-[10px] text-gray-500">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      ),
      action: { label: 'View Collaborations', path: '/collaborations' }
    },
    {
      id: 'done',
      title: 'You\'re All Set! 🚀',
      subtitle: 'Start exploring the platform',
      icon: Sparkles,
      color: 'from-pink-400 to-rose-500',
      content: (
        <div className="space-y-4 text-center">
          <p className="text-gray-600">
            You're ready to start using Snow Exchange. You can always come back to this guide from the menu.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full text-emerald-700 text-sm">
            <CheckCircle2 className="w-4 h-4" />
            Getting Started guide available in your menu
          </div>
        </div>
      )
    }
  ];

  const currentStepData = steps[currentStep];
  const Icon = currentStepData.icon;
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      onClose();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleAction = () => {
    if (currentStepData.action) {
      onClose();
      navigate(currentStepData.action.path);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        {/* Header with gradient */}
        <div className={`bg-gradient-to-br ${currentStepData.color} p-6 text-white relative`}>
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{currentStepData.title}</h2>
              <p className="text-white/80 text-sm">{currentStepData.subtitle}</p>
            </div>
          </div>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mt-4">
            {steps.map((_, i) => (
              <div 
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === currentStep ? 'w-6 bg-white' : 'w-1.5 bg-white/40'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {currentStepData.content}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex items-center justify-between gap-3">
          <div>
            {!isFirstStep && (
              <Button
                variant="ghost"
                onClick={handlePrev}
                className="text-gray-500"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {currentStepData.action && (
              <Button
                variant="outline"
                onClick={handleAction}
              >
                {currentStepData.action.label}
              </Button>
            )}
            <Button
              onClick={handleNext}
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              {isLastStep ? 'Get Started' : 'Next'}
              {!isLastStep && <ArrowRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>

        {/* Skip link */}
        {!isLastStep && (
          <div className="text-center pb-4">
            <button 
              onClick={onClose}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              Skip for now — I'll explore on my own
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingWizard;
